import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { toast } from 'react-hot-toast';
import { Alert, Spinner } from 'react-bootstrap';
import { EXCHANGE_MICROSOFT_SESSION_CODE } from 'services/api/auth';
import { setMicrosoftAccountId } from 'services/api/base';

type SessionAuthPayload = {
  success?: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresOn?: string | null;
  account?: {
    homeAccountId?: string;
    environment?: string;
    tenantId?: string;
    username?: string;
    localAccountId?: string;
  };
  user?: {
    id?: string;
    displayName?: string;
    email?: string;
    jobTitle?: string;
    department?: string;
    officeLocation?: string;
    userPrincipalName?: string;
  };
  permissions?: string[];
  error_description?: string;
};

const MicrosoftAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState<boolean>(false);
  const { refreshSession, authUser } = useAccessToken();

  const finalizeAuthentication = useCallback(
    async (payload: SessionAuthPayload) => {
      if (!payload || payload.success === false) {
        throw new Error(
          payload?.error_description ||
            'Authentication failed. Please try again.'
        );
      }

      const homeAccountId = payload?.account?.homeAccountId;
      if (typeof homeAccountId === 'string' && homeAccountId.trim()) {
        setMicrosoftAccountId(homeAccountId.trim());
      }

      localStorage.removeItem('microsoftAuthInProgress');
      setHasProcessed(true);
      toast.success('Microsoft authentication successful!');
      const refreshed = await refreshSession();
      if (!refreshed) {
        throw new Error('Could not load your session after sign-in.');
      }

      if (window.location.pathname !== '/home/apps/job/project-card-view') {
        navigate('/home/apps/job/project-card-view', { replace: true });
      }
    },
    [navigate, refreshSession]
  );

  const processedLocationSignatureRef = useRef<string | null>(null);
  const locationSignature = `${location.pathname}|${location.search}|${location.hash}`;

  const handleSessionExchange = useCallback(
    async (sessionCode: string) => {
      const trimmedCode = sessionCode?.trim();
      if (!trimmedCode) {
        throw new Error('Invalid session code returned from server');
      }

      try {
        const response = await EXCHANGE_MICROSOFT_SESSION_CODE(trimmedCode);
        const payload =
          (response?.data && response.data.data) || response?.data || response;

        if (!payload) {
          throw new Error('Empty session exchange response');
        }

        await finalizeAuthentication(payload as SessionAuthPayload);
      } catch (exchangeError: any) {
        const message =
          exchangeError?.response?.data?.message ||
          exchangeError?.response?.data?.error_description ||
          exchangeError?.message ||
          'Failed to exchange session code';
        throw new Error(message);
      }
    },
    [finalizeAuthentication]
  );

  const handleLegacyPayload = useCallback(
    async (encodedData: string) => {
      const sanitizedData = encodedData?.trim();

      if (!sanitizedData) {
        throw new Error('Empty authentication payload received');
      }

      const base64Pattern = /^[A-Za-z0-9+/=]+$/;
      if (!base64Pattern.test(sanitizedData)) {
        throw new Error('Authentication payload is not valid base64');
      }

      let decoded = '';
      try {
        decoded = atob(sanitizedData);
      } catch {
        throw new Error('Authentication payload could not be decoded');
      }

      let parsedPayload: SessionAuthPayload | null = null;
      try {
        parsedPayload = JSON.parse(decoded);
      } catch {
        throw new Error('Authentication payload is not valid JSON');
      }

      await finalizeAuthentication(parsedPayload as SessionAuthPayload);
    },
    [finalizeAuthentication]
  );

  useEffect(() => {
    if (hasProcessed) {
      return;
    }

    if (processedLocationSignatureRef.current === locationSignature) {
      return;
    }
    processedLocationSignatureRef.current = locationSignature;

    const processCallback = async () => {
      try {
        if (location.pathname === '/home/apps/job/project-card-view') {
          navigate('/home/apps/job/project-card-view', { replace: true });
          return;
        }

        const authInProgress = localStorage.getItem('microsoftAuthInProgress');

        if (authUser && !authInProgress) {
          navigate('/home/apps/job/project-card-view', { replace: true });
          return;
        }

        if (!authInProgress) {
          navigate('/');
          return;
        }

        const hashValue = location.hash.startsWith('#')
          ? location.hash.substring(1)
          : location.hash;
        const hashParams = new URLSearchParams(hashValue);
        const queryParams = new URLSearchParams(location.search);
        const paramSources = [hashParams, queryParams];
        const getParam = (keys: string[]): string | null => {
          for (const params of paramSources) {
            for (const key of keys) {
              const value = params.get(key);
              if (value) {
                return value;
              }
            }
          }
          return null;
        };

        const errorParam = getParam(['error']);
        if (errorParam) {
          const description =
            getParam(['error_description', 'errorDescription']) || errorParam;
          localStorage.removeItem('microsoftAuthInProgress');
          setError(description);
          setHasProcessed(true);
          return;
        }

        const sessionCode = getParam(['sessionCode', 'session_code']);
        if (sessionCode) {
          await handleSessionExchange(sessionCode);
          return;
        }

        const dataParam = getParam(['data']);
        if (dataParam) {
          await handleLegacyPayload(dataParam);
          return;
        }

        throw new Error('No authentication payload received');
      } catch (callbackError: any) {
        localStorage.removeItem('microsoftAuthInProgress');
        setError(callbackError?.message || 'An unexpected error occurred');
        setHasProcessed(true);
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [
    authUser,
    handleLegacyPayload,
    handleSessionExchange,
    hasProcessed,
    locationSignature,
    navigate
  ]);

  if (isProcessing) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <Spinner animation="border" role="status" className="mb-3">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="text-700">Processing Microsoft authentication...</p>
        <p className="text-600 fs-8">
          Please wait while we complete your sign-in...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <Alert variant="danger" className="w-100 max-w-md">
          <Alert.Heading>Authentication Error</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <button
              className="btn btn-outline-danger"
              onClick={() => navigate('/')}
            >
              Back to Sign In
            </button>
          </div>
        </Alert>
      </div>
    );
  }

  return null;
};

export default MicrosoftAuthCallback;
